import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Target, 
  Users, 
  School, 
  ChefHat, 
  MapPin, 
  Building2, 
  Landmark,
  Star, 
  Bell, 
  Shield, 
  Network, 
  Layout,
  CheckCircle2,
  TrendingUp,
  Baby,
  Lock,
  Zap
} from 'lucide-react';

// English Version of all sections
export const VisionSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1519389950473-47ba0277781c" 
        alt="Vision" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-emerald-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6 flex flex-col md:flex-row items-center gap-6">
          <img 
            src="/eco-fox-head.png"
            alt="EcoLuncH Mascot"
            className="w-24 h-24 object-contain"
          />
          <div>
            <h2 className="text-4xl font-bold mb-3">Vision & Goals</h2>
            <p className="text-xl font-semibold">The School Nutrition Revolution in Quebec</p>
          </div>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-lg">
      <CardContent className="p-0 overflow-hidden rounded-lg">
        <video 
          controls 
          className="w-full"
          preload="metadata"
        >
          <source src="https://customer-assets.emergentagent.com/job_7f252a75-97cc-4ed7-9549-a7fb574469a2/artifacts/xg29f4mx_Pr%C3%A9sentation%20Ecolunch.mp4" type="video/mp4" />
        </video>
      </CardContent>
    </Card>

    <Card className="border-none shadow-xl">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center space-x-2">
          <Target className="w-6 h-6 text-green-600" />
          <span>General Objective</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-lg leading-relaxed text-gray-700">
          EcoLuncH is a <strong className="text-green-600">revolutionary digital infrastructure</strong> dedicated to durably transforming the management, distribution and monitoring of school meal quality <strong className="text-green-700">across all of Quebec</strong>.
        </p>
        <p className="text-lg leading-relaxed text-gray-700">
          It aims to <strong className="text-emerald-600">centralize, automate and simplify</strong> the entire sector by bringing together, within a single environment, parents, schools, caterers and school service centers (SSC). By creating a common, standardized, fluid, transparent and measurable framework, EcoLuncH positions school meals in a uniform logic across Quebec and <strong className="text-green-600">establishes itself as the market's reference infrastructure</strong>.
        </p>
      </CardContent>
    </Card>

    <div className="grid md:grid-cols-2 gap-6">
      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-green-600">Functional Description</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            EcoLuncH acts as a <strong>school food governance platform</strong> based on a multi-level model:
          </p>
          <ul className="space-y-2">
            <li className="flex items-center space-x-2">
              <Badge className="bg-purple-500"><Users className="w-4 h-4" /></Badge>
              <span>Parent/Child: intuitive family interface</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-orange-500"><ChefHat className="w-4 h-4" /></Badge>
              <span>Caterer: production back-office</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-indigo-500"><School className="w-4 h-4" /></Badge>
              <span>School: tracking and communication cockpit</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-teal-500"><Building2 className="w-4 h-4" /></Badge>
              <span>School Directions: supervise one or more schools / establishments</span>
            </li>
            <li className="flex items-center space-x-2">
              <Badge className="bg-red-500"><Landmark className="w-4 h-4" /></Badge>
              <span>SSC: global governance</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
        <CardHeader>
          <CardTitle className="text-xl text-emerald-600">Core Principles</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Intelligent Automation</strong>
                <p className="text-sm text-gray-600">Reduction of manual work and double entry</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Total Transparency</strong>
                <p className="text-sm text-gray-600">Complete traceability of every meal</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Fluid Hierarchy</strong>
                <p className="text-sm text-gray-600">Controlled information flow</p>
              </div>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <strong>Absolute Security</strong>
                <p className="text-sm text-gray-600">Data compartmentalized by entity</p>
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>

    <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-emerald-50">
      <CardHeader>
        <CardTitle className="text-xl">Objectives by Stakeholder</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-green-200">
                <th className="text-left p-3 font-semibold">Stakeholder</th>
                <th className="text-left p-3 font-semibold">Main Objective</th>
                <th className="text-left p-3 font-semibold">Added Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-green-100">
              <tr>
                <td className="p-3"><Users className="w-4 h-4 inline text-cyan-400" /> Parents</td>
                <td className="p-3">Order quickly</td>
                <td className="p-3 text-green-600 font-medium">Time saving, visibility</td>
              </tr>
              <tr>
                <td className="p-3"><Baby className="w-4 h-4 inline text-pink-400" /> Children</td>
                <td className="p-3">Participate through play</td>
                <td className="p-3 text-green-600 font-medium">Playful involvement</td>
              </tr>
              <tr>
                <td className="p-3"><ChefHat className="w-4 h-4 inline text-orange-400" /> Caterers</td>
                <td className="p-3">Automate management</td>
                <td className="p-3 text-green-600 font-medium">Operational efficiency</td>
              </tr>
              <tr>
                <td className="p-3"><School className="w-4 h-4 inline text-green-400" /> Schools</td>
                <td className="p-3">Monitor satisfaction</td>
                <td className="p-3 text-green-600 font-medium">Management tool</td>
              </tr>
              <tr>
                <td className="p-3"><Building2 className="w-4 h-4 inline text-teal-400" /> School Directions</td>
                <td className="p-3">Supervise one or more schools</td>
                <td className="p-3 text-green-600 font-medium">Consolidated view</td>
              </tr>
              <tr>
                <td className="p-3"><Landmark className="w-4 h-4 inline text-red-400" /> SSC</td>
                <td className="p-3">Govern and audit</td>
                <td className="p-3 text-green-600 font-medium">Strategic vision</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardHeader>
        <CardTitle className="text-xl">Usage Example</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
            <p className="text-gray-700">A parent orders for two children in <strong className="text-purple-600">15 seconds</strong> via mobile app</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
            <p className="text-gray-700">The caterer receives the order <strong className="text-orange-600">automatically</strong> integrated into production sheet</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
            <p className="text-gray-700">The school sees volumes adjusted on dashboard in <strong className="text-indigo-600">real-time</strong></p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">4</div>
            <p className="text-gray-700">SSC sees a <strong className="text-red-600">xx%</strong> participation increase in the region</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

export const ConclusionSectionEN = () => (
  <div className="space-y-6 animate-fadeIn">
    <div className="relative overflow-hidden rounded-2xl h-64 shadow-2xl">
      <img 
        src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c" 
        alt="Conclusion" 
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-green-600/90 to-teal-600/90 flex items-center justify-center">
        <div className="text-center text-white p-6">
          <CheckCircle2 className="w-20 h-20 mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">Conclusion</h2>
          <p className="text-xl">Much More Than a Simple Platform</p>
        </div>
      </div>
    </div>

    <Card className="border-none shadow-2xl bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <CardContent className="py-12">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">EcoLuncH</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Is not just a simple ordering system —
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-green-600 mb-3">A Complete Infrastructure</h3>
              <p className="text-gray-700">
                For <strong>structural transformation</strong> of the school food model
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-lg shadow-md">
                <Lock className="w-8 h-8 text-green-600 mb-2" />
                <h4 className="font-bold text-gray-800 mb-2">Secured</h4>
                <p className="text-sm text-gray-600">Absolute data protection</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <Zap className="w-8 h-8 text-yellow-500 mb-2" />
                <h4 className="font-bold text-gray-800 mb-2">Automated</h4>
                <p className="text-sm text-gray-600">Reduction of manual work</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-md">
                <TrendingUp className="w-8 h-8 text-blue-600 mb-2" />
                <h4 className="font-bold text-gray-800 mb-2">Scalable</h4>
                <p className="text-sm text-gray-600">Adapts to needs</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-teal-600 mb-3">For Each Stakeholder</h3>
              <p className="text-gray-700 mb-4">
                <strong>Tools adapted to their role</strong>
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Badge className="bg-purple-500 text-white px-4 py-2">Parents</Badge>
                <Badge className="bg-pink-500 text-white px-4 py-2">Children</Badge>
                <Badge className="bg-indigo-500 text-white px-4 py-2">Schools</Badge>
                <Badge className="bg-orange-500 text-white px-4 py-2">Caterers</Badge>
                <Badge className="bg-teal-500 text-white px-4 py-2">Direction</Badge>
                <Badge className="bg-red-500 text-white px-4 py-2">SSC</Badge>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-600 to-teal-600 p-8 rounded-xl shadow-xl text-white">
              <h3 className="text-2xl font-bold mb-3">Centralized Governance</h3>
              <p className="text-lg">
                <strong>Measurable</strong> · <strong>Adjustable</strong> · <strong>Transparent</strong>
              </p>
              <p className="mt-4 text-green-50">
                Providing SSC with a complete strategic vision — The School Nutrition Revolution in Quebec
              </p>
            </div>
          </div>

          <div className="pt-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="w-full"
              >
                <source src="https://customer-assets.emergentagent.com/job_7f252a75-97cc-4ed7-9549-a7fb574469a2/artifacts/bq1qtgws_EcoLuncH%20Animated.mp4" type="video/mp4" />
              </video>
              <div className="px-8 py-6 text-center">
                <p className="text-gray-600 font-semibold">The School Nutrition Revolution in Quebec</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border-none shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
      <CardContent className="py-8">
        <div className="text-center space-y-4">
          <img 
            src="/ecolunch-logo.png"
            alt="EcoLuncH Logo"
            className="h-16 mx-auto mb-4"
          />
          <h3 className="text-xl font-bold text-gray-800">Ready to Discover More?</h3>
          <p className="text-gray-600">Explore the different sections of the media kit to learn more about EcoLuncH</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Badge variant="outline" className="px-4 py-2"><Target className="w-3 h-3 inline text-green-400" /> Vision</Badge>
            <Badge variant="outline" className="px-4 py-2"><Network className="w-3 h-3 inline text-green-400" /> Architecture</Badge>
            <Badge variant="outline" className="px-4 py-2"><ChefHat className="w-3 h-3 inline text-green-400" /> Caterer</Badge>
            <Badge variant="outline" className="px-4 py-2"><Users className="w-4 h-4 inline text-cyan-400" /> Parents</Badge>
            <Badge variant="outline" className="px-4 py-2"><School className="w-3 h-3 inline text-green-400" /> School</Badge>
            <Badge variant="outline" className="px-4 py-2"><Star className="w-3 h-3 inline text-yellow-400" /> Evaluation</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);
